import { useState, useEffect, useCallback, useRef } from 'react';
import { alphalendWebSocketService } from '../services/alphalendWebsocket';
import { useAccountHistorySync } from './useAccountHistorySync';
// Action types for account summary requests
const AccountSummaryActions = {
    GetAccountSummary: 'get_account_summary',
    GetTokenEarnings: 'get_token_earnings',
    GetSupplyPositions: 'get_supply_positions',
    GetBorrowPositions: 'get_borrow_positions',
};
// Helper hook to track connection status
function useConnectionStatus() {
    const [isConnected, setIsConnected] = useState(alphalendWebSocketService.getConnectionStatus() === 'connected');
    useEffect(() => {
        const unsubscribe = alphalendWebSocketService.onConnectionStatusChange(() => {
            setIsConnected(alphalendWebSocketService.getConnectionStatus() === 'connected');
        });
        return unsubscribe;
    }, []);
    return isConnected;
}
export function useAccountSummary(positionCapId) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const isConnected = useConnectionStatus();
    const hasFetchedRef = useRef(false);
    // Track sync progress
    const syncProgress = useAccountHistorySync(positionCapId);
    // Listen for response messages
    useEffect(() => {
        if (!positionCapId)
            return;
        const handleResponse = (message) => {
            if (!(message === null || message === void 0 ? void 0 : message.data))
                return;
            const responseData = message.data;
            // Verify this response is for our position
            if (responseData.position_id !== positionCapId)
                return;
            setData((prevData) => {
                if (JSON.stringify(prevData) === JSON.stringify(responseData)) {
                    return prevData;
                }
                return responseData;
            });
            setLoading(false);
            setError(null);
        };
        const handleError = (message) => {
            if ((message === null || message === void 0 ? void 0 : message.data) && typeof message.data === 'object' && 'message' in message.data) {
                setError(message.data.message);
                setLoading(false);
            }
        };
        const unsubscribeResponse = alphalendWebSocketService.addMessageListener('account_summary_response', handleResponse);
        const unsubscribeError = alphalendWebSocketService.addMessageListener('error', handleError);
        return () => {
            unsubscribeResponse();
            unsubscribeError();
        };
    }, [positionCapId]);
    // Fetch data when positionCapId changes or connection opens
    useEffect(() => {
        if (positionCapId && isConnected && !hasFetchedRef.current) {
            setLoading(true);
            setError(null);
            hasFetchedRef.current = true;
            alphalendWebSocketService.sendMessage({
                action: AccountSummaryActions.GetAccountSummary,
                position_cap_id: positionCapId,
            });
        }
        // Reset fetch flag when positionCapId changes
        if (!positionCapId) {
            hasFetchedRef.current = false;
        }
    }, [positionCapId, isConnected]);
    // Reset fetch flag when positionCapId changes
    useEffect(() => {
        hasFetchedRef.current = false;
    }, [positionCapId]);
    const refetch = useCallback(() => {
        if (positionCapId && isConnected) {
            setLoading(true);
            setError(null);
            alphalendWebSocketService.sendMessage({
                action: AccountSummaryActions.GetAccountSummary,
                position_cap_id: positionCapId,
            });
        }
    }, [positionCapId, isConnected]);
    // Auto-refresh when sync completes successfully
    useEffect(() => {
        if (syncProgress.shouldRefresh) {
            refetch();
        }
    }, [syncProgress.shouldRefresh, refetch]);
    return {
        data,
        loading: loading || (syncProgress.isActive && !data),
        error,
        refetch,
        isConnected,
        syncStatus: syncProgress.status,
    };
}
export function useTokenEarnings(positionCapId) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const isConnected = useConnectionStatus();
    const hasFetchedRef = useRef(false);
    const syncProgress = useAccountHistorySync(positionCapId);
    // Listen for response messages
    useEffect(() => {
        if (!positionCapId)
            return;
        const handleResponse = (message) => {
            if (!(message === null || message === void 0 ? void 0 : message.data))
                return;
            const responseData = message.data;
            setData((prevData) => {
                if (JSON.stringify(prevData) === JSON.stringify(responseData)) {
                    return prevData;
                }
                return responseData;
            });
            setLoading(false);
            setError(null);
        };
        const handleError = (message) => {
            if ((message === null || message === void 0 ? void 0 : message.data) && typeof message.data === 'object' && 'message' in message.data) {
                setError(message.data.message);
                setLoading(false);
            }
        };
        const unsubscribeResponse = alphalendWebSocketService.addMessageListener('token_earnings_response', handleResponse);
        const unsubscribeError = alphalendWebSocketService.addMessageListener('error', handleError);
        return () => {
            unsubscribeResponse();
            unsubscribeError();
        };
    }, [positionCapId]);
    // Fetch data when positionCapId changes or connection opens
    useEffect(() => {
        if (positionCapId && isConnected && !hasFetchedRef.current) {
            setLoading(true);
            setError(null);
            hasFetchedRef.current = true;
            alphalendWebSocketService.sendMessage({
                action: AccountSummaryActions.GetTokenEarnings,
                position_cap_id: positionCapId,
            });
        }
        if (!positionCapId) {
            hasFetchedRef.current = false;
        }
    }, [positionCapId, isConnected]);
    useEffect(() => {
        hasFetchedRef.current = false;
    }, [positionCapId]);
    const refetch = useCallback(() => {
        if (positionCapId && isConnected) {
            setLoading(true);
            setError(null);
            alphalendWebSocketService.sendMessage({
                action: AccountSummaryActions.GetTokenEarnings,
                position_cap_id: positionCapId,
            });
        }
    }, [positionCapId, isConnected]);
    useEffect(() => {
        if (syncProgress.shouldRefresh) {
            refetch();
        }
    }, [syncProgress.shouldRefresh, refetch]);
    return {
        data,
        loading: loading || (syncProgress.isActive && !data),
        error,
        refetch,
        isConnected,
        syncStatus: syncProgress.status,
    };
}
export function useSupplyPositions(positionCapId) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const isConnected = useConnectionStatus();
    const hasFetchedRef = useRef(false);
    // Track sync progress
    const syncProgress = useAccountHistorySync(positionCapId);
    // Listen for response messages
    useEffect(() => {
        if (!positionCapId)
            return;
        const handleResponse = (message) => {
            if (!(message === null || message === void 0 ? void 0 : message.data))
                return;
            const responseData = message.data;
            setData((prevData) => {
                if (JSON.stringify(prevData) === JSON.stringify(responseData)) {
                    return prevData;
                }
                return responseData;
            });
            setLoading(false);
            setError(null);
        };
        const handleError = (message) => {
            if ((message === null || message === void 0 ? void 0 : message.data) && typeof message.data === 'object' && 'message' in message.data) {
                setError(message.data.message);
                setLoading(false);
            }
        };
        const unsubscribeResponse = alphalendWebSocketService.addMessageListener('supply_positions_response', handleResponse);
        const unsubscribeError = alphalendWebSocketService.addMessageListener('error', handleError);
        return () => {
            unsubscribeResponse();
            unsubscribeError();
        };
    }, [positionCapId]);
    // Fetch data when positionCapId changes or connection opens
    useEffect(() => {
        if (positionCapId && isConnected && !hasFetchedRef.current) {
            setLoading(true);
            setError(null);
            hasFetchedRef.current = true;
            alphalendWebSocketService.sendMessage({
                action: AccountSummaryActions.GetSupplyPositions,
                position_cap_id: positionCapId,
            });
        }
        if (!positionCapId) {
            hasFetchedRef.current = false;
        }
    }, [positionCapId, isConnected]);
    useEffect(() => {
        hasFetchedRef.current = false;
    }, [positionCapId]);
    const refetch = useCallback(() => {
        if (positionCapId && isConnected) {
            setLoading(true);
            setError(null);
            alphalendWebSocketService.sendMessage({
                action: AccountSummaryActions.GetSupplyPositions,
                position_cap_id: positionCapId,
            });
        }
    }, [positionCapId, isConnected]);
    // Auto-refresh when sync completes successfully
    useEffect(() => {
        if (syncProgress.shouldRefresh) {
            refetch();
        }
    }, [syncProgress.shouldRefresh, refetch]);
    return {
        data,
        loading: loading || (syncProgress.isActive && !data),
        error,
        refetch,
        isConnected,
        syncStatus: syncProgress.status,
    };
}
export function useBorrowPositions(positionCapId) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const isConnected = useConnectionStatus();
    const hasFetchedRef = useRef(false);
    // Track sync progress
    const syncProgress = useAccountHistorySync(positionCapId);
    // Listen for response messages
    useEffect(() => {
        if (!positionCapId)
            return;
        const handleResponse = (message) => {
            if (!(message === null || message === void 0 ? void 0 : message.data))
                return;
            const responseData = message.data;
            setData((prevData) => {
                if (JSON.stringify(prevData) === JSON.stringify(responseData)) {
                    return prevData;
                }
                return responseData;
            });
            setLoading(false);
            setError(null);
        };
        const handleError = (message) => {
            if ((message === null || message === void 0 ? void 0 : message.data) && typeof message.data === 'object' && 'message' in message.data) {
                setError(message.data.message);
                setLoading(false);
            }
        };
        const unsubscribeResponse = alphalendWebSocketService.addMessageListener('borrow_positions_response', handleResponse);
        const unsubscribeError = alphalendWebSocketService.addMessageListener('error', handleError);
        return () => {
            unsubscribeResponse();
            unsubscribeError();
        };
    }, [positionCapId]);
    // Fetch data when positionCapId changes or connection opens
    useEffect(() => {
        if (positionCapId && isConnected && !hasFetchedRef.current) {
            setLoading(true);
            setError(null);
            hasFetchedRef.current = true;
            alphalendWebSocketService.sendMessage({
                action: AccountSummaryActions.GetBorrowPositions,
                position_cap_id: positionCapId,
            });
        }
        if (!positionCapId) {
            hasFetchedRef.current = false;
        }
    }, [positionCapId, isConnected]);
    useEffect(() => {
        hasFetchedRef.current = false;
    }, [positionCapId]);
    const refetch = useCallback(() => {
        if (positionCapId && isConnected) {
            setLoading(true);
            setError(null);
            alphalendWebSocketService.sendMessage({
                action: AccountSummaryActions.GetBorrowPositions,
                position_cap_id: positionCapId,
            });
        }
    }, [positionCapId, isConnected]);
    // Auto-refresh when sync completes successfully
    useEffect(() => {
        if (syncProgress.shouldRefresh) {
            refetch();
        }
    }, [syncProgress.shouldRefresh, refetch]);
    return {
        data,
        loading: loading || (syncProgress.isActive && !data),
        error,
        refetch,
        isConnected,
        syncStatus: syncProgress.status,
    };
}
